import { useEffect, useState, useContext, useMemo } from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography, Box, IconButton, Tooltip, Chip, Radio, TablePagination, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { TableSkeletonLoader } from '../loaders';
import { Booking, LoadingPlaceTypes, BookingStatus, BookingStatusList, LoadingPlace } from '../../models/booking';
import { Vehicle } from '../../models/vehicle';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import PrintIcon from '@mui/icons-material/Print';
import WarningIcon from '@mui/icons-material/Warning';
import { apiPost, handleApiError } from '../../services/apiClient';
import { updateVehicle, getVehicle } from '../../services/vehicleService';
import { formatDateTime } from '../../utils/misc';
import { VehicleAutoSearch } from '../../models/vehicleAutoSearch';
import { VehicleAutoNegoSession } from '../../models/vehicleAutoNegoSession';
import { toast } from '../../utils/toast';
import { useTranslation } from 'react-i18next';
import { PrimaryButton, SecondaryButton } from '../buttons';
import { useBookings, useTenant } from '../../hooks';
import { AuthContext } from '../../context/authContext';
import { UserPlatformSubRole } from '../../models/user';
import { fillBookingTemplate, printBookingDocument } from '../../utils/bookingTemplate';
import { COUNTRY_NAMES } from '../../models/country';
import { DEFAULT_LOADING_PLACES } from '../../pages/bookings/components/form';
import SearchInput from '../inputs/searchInput';
import { TotalCountBadge } from '../badges';

/**
 * Props for BookingsTable component.
 * @param vehicle Optional vehicle to filter bookings by truck_id.
 * @param autoSearchSession Optional pre-fetched auto search session (to avoid duplicate API calls)
 * @param autoNegoSession Optional pre-fetched auto nego session (to avoid duplicate API calls)
 * @param onVehicleUpdate Optional callback to notify parent when vehicle data should be refreshed
 * @param standalone Whether to show as standalone page mode (with truck column, server pagination, header controls)
 * @param showHeader Whether to show the header section with title and buttons
 * @param tenantTemplate Optional pre-fetched tenant template (to avoid duplicate API calls)
 */
export interface BookingsTableProps {
  vehicle?: Vehicle | null;
  autoSearchSession?: VehicleAutoSearch | null;
  autoNegoSession?: VehicleAutoNegoSession | null;
  onVehicleUpdate?: () => void;
  standalone?: boolean;
  showHeader?: boolean;
  tenantTemplate?: string;
}

/**
 * Unified table component to display bookings.
 * Can be used in two modes:
 * 1. Vehicle mode (vehicle prop provided): Shows bookings for a specific vehicle with "set as current" feature
 * 2. Standalone mode (standalone=true): Shows all bookings with truck column and server-side pagination
 * @param vehicle Optional vehicle to filter bookings by truck_id.
 * @param autoSearchSession Optional pre-fetched auto search session
 * @param autoNegoSession Optional pre-fetched auto nego session
 * @param onVehicleUpdate Optional callback to notify parent when vehicle data should be refreshed
 * @param standalone Whether to show as standalone page mode
 * @param showHeader Whether to show the header section (default true)
 * @param tenantTemplate Optional pre-fetched tenant template
 */
export default function BookingsTable({
  vehicle,
  onVehicleUpdate,
  standalone = false,
  showHeader = true,
  tenantTemplate: tenantTemplateProp
}: BookingsTableProps) {
  const { t } = useTranslation();
  const authContext = useContext(AuthContext);
  const { tenant } = useTenant();
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');

  // Use template from tenant via hook or prop
  const tenantTemplate = tenantTemplateProp ?? tenant?.bookingFileHtmlTemplate ?? '';

  // Check if user is a truck driver (should not see delete button)
  const isDriver = authContext?.user?.subRole === UserPlatformSubRole.TRUCK_DRIVER;

  // Helper function to get status label and color
  const getStatusInfo = (status: BookingStatus) => {
    const statusInfo = BookingStatusList.find(item => item.value === status);
    return statusInfo || { key: 'Unknown', color: 'default' };
  };

  // Handle loading sheet download as PDF via browser native print dialog
  const handleDownloadLoadingSheet = (e: React.MouseEvent, booking: Booking) => {
    e.stopPropagation();
    if (!tenantTemplate) {
      toast.error(t('No loading sheet template configured. Please set up a template in tenant settings.'));
      return;
    }
    const vehiclePlate = vehicle?.truckPlate || (booking as any).vehicle?.truckPlate;
    const trailerPlate = vehicle?.trailerPlate || (booking as any).vehicle?.trailerPlate;
    const filledTemplate = fillBookingTemplate(tenantTemplate, booking, undefined, vehiclePlate, undefined, trailerPlate);
    printBookingDocument(filledTemplate);
    toast.info(t('Select "Save as PDF" in the print dialog to download as PDF'), 6000);
  };

  // Handle loading sheet print
  const handlePrintLoadingSheet = (e: React.MouseEvent, booking: Booking) => {
    e.stopPropagation();
    if (!tenantTemplate) {
      toast.error(t('No loading sheet template configured. Please set up a template in tenant settings.'));
      return;
    }
    const vehiclePlate = vehicle?.truckPlate || (booking as any).vehicle?.truckPlate;
    const trailerPlate = vehicle?.trailerPlate || (booking as any).vehicle?.trailerPlate;
    const filledTemplate = fillBookingTemplate(tenantTemplate, booking, undefined, vehiclePlate, undefined, trailerPlate);
    printBookingDocument(filledTemplate);
  };

  // Check if a booking has non-geocoded loading places (null lat/long coordinates)
  const hasNonGeocodedLocations = (booking: Booking): boolean => booking.loadingPlaces?.some(place => !place.address?.geoCoordinate?.latitude || !place.address?.geoCoordinate?.longitude
  ) || false;

  const {
    bookings,
    loading,
    DeleteModal,
    Dialog,
    submitting,
    fetchBookings,
    handleDelete,
    setIsOpen,
    setFormData,
    setAction,
    setHasValidationErrors,
    query,
    setQuery,
    rowCount,
    paginationModel,
    setPaginationModel,
  } = useBookings({
    vehicle: vehicle ?? undefined,
    standalone,
    onBookingCreated: async () => {
      if (vehicle?.id) {
        try {
          const freshVehicle = await getVehicle(vehicle.id);
          setCurrentBookingId(freshVehicle.current_booking_id);
        } catch (err) {
          console.error('[BookingsTable] Failed to fetch vehicle after booking create', err);
        }
      }
    }
  });

  const [currentBookingId, setCurrentBookingId] = useState<number | undefined>(vehicle?.current_booking_id);

  useEffect(() => { // update currentBookingId when vehicle prop changes
    setCurrentBookingId(vehicle?.current_booking_id);
  }, [vehicle?.current_booking_id]);

  useEffect(() => { // refresh after submitting booking form (non-standalone only)
    if (!submitting && !standalone) {
      fetchBookings(vehicle?.id);
    }
  }, [fetchBookings, submitting, vehicle?.id, standalone]);

  // Refetch when query or pagination changes in standalone mode
  useEffect(() => {
    if (standalone) {
      fetchBookings();
    }
  }, [standalone, query, paginationModel, fetchBookings]);

  const handleBookingClick = (id: number | undefined) => {
    if (!id) return;
    const booking = bookings.find(item => item.id === id);
    if (booking) {
      setAction('update');
      if (vehicle) {
        setFormData({ ...booking, vehicle });
      } else {
        setFormData(booking);
      }
      setHasValidationErrors(false);
      setIsOpen(true);
    }
  };

  const handleSetAsCurrent = async (booking: Booking) => {
    if (!vehicle) return;

    // Update UI immediately (optimistic update)
    setCurrentBookingId(booking.id);

    try {
      await updateVehicle(vehicle.id, { current_booking_id: booking.id });
      toast.success(t('Booking set as current successfully'));
      onVehicleUpdate?.();
    } catch (error) {
      // Revert on error
      setCurrentBookingId(vehicle?.current_booking_id);
      handleApiError(error, t('Failed to set booking as current'));
    }
  };

  const handleSyncVehiclePosition = async () => {
    if (!vehicle) return;
    try {
      await apiPost(`${process.env.REACT_APP_API_URL}/api/v1/vehicle/${vehicle.id}/sync_location`);
    } catch {
      toast.error(t('Failed to sync vehicle position'));
    }
  };

  // Handle search query change in standalone mode
  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    if (standalone) {
      setPaginationModel((prev: any) => ({ ...prev, page: 0 }));
      setQuery((prev: any) => {
        const nextQuery = { ...prev };
        const trimmedValue = value.trim();
        if (trimmedValue) {
          nextQuery.search = trimmedValue;
        } else {
          delete nextQuery.search;
          delete nextQuery.q;
          delete nextQuery.query;
        }
        return nextQuery;
      });
    }
  };

  // Handle status filter change in standalone mode
  const handleStatusFilterChange = (value: string) => {
    setStatusFilter(value);
    if (standalone) {
      setPaginationModel((prev: any) => ({ ...prev, page: 0 }));
      setQuery((prev: any) => {
        const newQuery = { ...prev };
        if (value) {
          const numericStatus = Number(value);
          newQuery.status = Number.isNaN(numericStatus) ? value : numericStatus;
        } else {
          delete newQuery.status;
        }
        return newQuery;
      });
    }
  };

  // Format loading place for display
  const formatLoadingPlace = (place?: LoadingPlace): string => {
    if (!place?.address) return '-';
    const addr = place.address;
    if (addr.city && addr.country) {
      return `${addr.city}, ${COUNTRY_NAMES[addr.country] || addr.country}`;
    }
    if (addr.postalCode && addr.city) {
      return `${addr.postalCode.substring(0, 2)} ${addr.city}`;
    }
    return '-';
  };

  // Status filter options for standalone mode
  const statusOptions = useMemo(() => [
    { key: t('All'), value: '' },
    ...BookingStatusList.map(s => ({ key: s.key, value: String(s.value) }))
  ], [t]);

  return (
    <Box>
      {showHeader && (
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          padding: standalone ? 0 : { xs: 2, sm: 2.5, md: 3 },
          alignItems: { xs: 'stretch', sm: 'center' },
          mb: 2,
          gap: { xs: 1.5, sm: 2 }
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: standalone ? 0 : 1, mb: { xs: 1, sm: 0 }, mr: standalone ? 2 : 0 }}>
            <Typography variant="h6" sx={{ fontSize: (theme: any) => theme.typography.fontSizes.heading2 }}>
              {t('Bookings')}
            </Typography>
            {standalone && <TotalCountBadge count={rowCount} />}
          </Box>

          {standalone && (
            <Box sx={{ display: 'flex', gap: 2, flexGrow: 1, alignItems: 'center' }}>
              <Box sx={{ minWidth: 200 }}>
                <SearchInput
                  placeholder={t('Search')}
                  value={searchQuery}
                  onChange={handleSearchChange}
                />
              </Box>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>{t('Status')}</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => handleStatusFilterChange(e.target.value)}
                  label={t('Status')}
                >
                  {statusOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.key}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}

          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'center' },
            gap: { xs: 1, sm: 2 },
            flexWrap: 'wrap',
            width: { xs: '100%', sm: 'auto' }
          }}>
            {vehicle && (
              <SecondaryButton onClick={handleSyncVehiclePosition}>
                {t('Sync Vehicle Position')}
              </SecondaryButton>
            )}
            <Box sx={{ display: 'inline-flex', opacity: vehicle?.isActive === false ? 0.4 : 1, pointerEvents: vehicle?.isActive === false ? 'none' : 'auto' }}>
              <PrimaryButton onClick={() => {
                setAction('create');
                setFormData(vehicle ? { vehicle, loadingPlaces: DEFAULT_LOADING_PLACES } : { loadingPlaces: DEFAULT_LOADING_PLACES });
                setHasValidationErrors(false);
                setIsOpen(true);
              }}>
                {t('Create')}
              </PrimaryButton>
            </Box>
          </Box>
        </Box>
      )}
      <TableContainer component={Paper} sx={{ borderRadius: 1.5 }}>
        <Table>
          <TableHead sx={{ bgcolor: 'background.lightGrey' }}>
            <TableRow>
              {!standalone && (
                <TableCell sx={{ fontWeight: 600, py: 1.25 }}>{t('Current')}</TableCell>
              )}
              {standalone && (
                <TableCell sx={{ fontWeight: 600, py: 1.25 }}>{t('Truck')}</TableCell>
              )}
              <TableCell sx={{ fontWeight: 600, py: 1.25 }}>{t('Start Place')}</TableCell>
              <TableCell sx={{ fontWeight: 600, py: 1.25 }}>{t('End Place')}</TableCell>
              <TableCell sx={{ fontWeight: 600, py: 1.25 }}>{t('Start Time')}</TableCell>
              <TableCell sx={{ fontWeight: 600, py: 1.25 }}>{t('End Time')}</TableCell>
              <TableCell sx={{ fontWeight: 600, py: 1.25 }}>€/km</TableCell>
              <TableCell sx={{ fontWeight: 600, py: 1.25 }}>{t('Approach KM')}</TableCell>
              {!standalone && (
                <TableCell sx={{ fontWeight: 600, py: 1.25 }}>{t('Score')}</TableCell>
              )}
              <TableCell sx={{ fontWeight: 600, py: 1.25 }}>{t('Status')}</TableCell>
              <TableCell sx={{ fontWeight: 600, py: 1.25 }}>{t('Created At')}</TableCell>
              <TableCell sx={{ fontWeight: 600, py: 1.25 }} align="right">{t('Actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableSkeletonLoader loading={loading} columns={standalone ? 10 : 11} rows={10}>
              {bookings.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={standalone ? 10 : 11} align="center" sx={{ py: 4 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'text.grey',
                        fontSize: (theme: any) => theme.typography.fontSizes.body,
                        fontStyle: 'italic',
                      }}
                    >
                      {t('No bookings found.')}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'text.grey',
                        fontSize: (theme: any) => theme.typography.fontSizes.small,
                        mt: 0.5,
                        display: 'block',
                        opacity: 0.7,
                      }}
                    >
                      {t('Add a booking to get started')}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                bookings.map((booking) => {
                  const loadingPlace = booking.loadingPlaces?.find(p => p.loadingType === LoadingPlaceTypes.LOADING) || booking.loadingPlaces?.[0];
                  const unloadingPlace = booking.loadingPlaces?.find(p => p.loadingType === LoadingPlaceTypes.UNLOADING) || booking.loadingPlaces?.[1];
                  const isCurrentBooking = !standalone && currentBookingId === booking.id;
                  const isDelayed = standalone && (booking.delayed ?? false);
                  const isCancelled = booking.status === BookingStatus.CANCELLED;
                  const bookingVehicle = (booking as any).vehicle;

                  return (
                    <TableRow
                      key={booking.id}
                      onClick={() => handleBookingClick(booking.id)}
                      sx={{
                        cursor: 'pointer',
                        backgroundColor: () => isCurrentBooking ? 'rgba(139, 92, 246, 0.05)' : 'inherit',
                        borderLeft: (theme: Theme) => isCurrentBooking ? `4px solid ${theme.palette.text.purple}` : 'none',
                        '&:hover': {
                          backgroundColor: () => isCurrentBooking ? 'rgba(139, 92, 246, 0.08)' : 'action.hover'
                        },
                        opacity: isCancelled && !isCurrentBooking ? 0.5 : 1,
                        transition: 'opacity 0.2s ease-in-out',
                        '& .MuiTableCell-root': {
                          py: 1,
                          verticalAlign: 'middle',
                        }
                      }}
                    >
                      {!standalone && (
                        <TableCell onClick={(e) => e.stopPropagation()} sx={{ py: 1 }}>
                          <Tooltip title={t('Set as current booking (Auto Search Reference)')}>
                            <Radio
                              checked={isCurrentBooking}
                              onChange={() => handleSetAsCurrent(booking)}
                              value={booking.id}
                              name="current-booking"
                              size="small"
                              sx={{
                                '&.Mui-checked': {
                                  color: 'text.purple',
                                },
                              }}
                            />
                          </Tooltip>
                        </TableCell>
                      )}
                      {standalone && (
                        <TableCell sx={{ py: 1, fontWeight: 600 }}>
                          {bookingVehicle ? (
                            `${bookingVehicle.truckPlate || 'N/A'}${bookingVehicle.trailerPlate ? ' / ' + bookingVehicle.trailerPlate : ''}`
                          ) : 'N/A'}
                        </TableCell>
                      )}
                      <TableCell sx={{ py: 1 }}>{formatLoadingPlace(loadingPlace)}</TableCell>
                      <TableCell sx={{ py: 1 }}>{formatLoadingPlace(unloadingPlace)}</TableCell>
                      <TableCell sx={{ py: 1 }}>{booking.startTime ? formatDateTime(booking.startTime) : '-'}</TableCell>
                      <TableCell sx={{ py: 1 }}>{booking.endTime ? formatDateTime(booking.endTime) : '-'}</TableCell>
                      <TableCell sx={{ py: 1 }}>
                        {(() => {
                          if (!((booking.source ?? 0) > 0 && booking.price && (booking.distanceKms ?? 0) > 0)) return '—';
                          const eurPerKm = booking.price / booking.distanceKms!;
                          const threshold = tenant?.targetPriceMarginFactor;
                          const color = threshold ? (eurPerKm >= threshold ? 'text.successGreen' : 'text.red') : undefined;
                          return <Typography component="span" sx={{ color, fontSize: 'inherit', fontWeight: color ? 600 : 400 }}>{eurPerKm.toFixed(2)}</Typography>;
                        })()}
                      </TableCell>
                      <TableCell sx={{ py: 1 }}>
                        {(() => {
                          if (!((booking.source ?? 0) > 0 && booking.approachDistanceKms && booking.approachDistanceKms > 0)) return '—';
                          const km = booking.approachDistanceKms;
                          const color = km <= 85 ? 'text.successGreen' : km <= 150 ? 'warning.main' : 'text.red';
                          return <Typography component="span" sx={{ color, fontSize: 'inherit', fontWeight: 600 }}>{km.toFixed(0)}</Typography>;
                        })()}
                      </TableCell>
                      {!standalone && (
                        <TableCell sx={{ py: 1 }}>{booking.score !== null ? booking.score : '-'}</TableCell>
                      )}
                      <TableCell sx={{ py: 1 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-start' }}>
                          <Chip
                            label={getStatusInfo(booking.status).key}
                            size="small"
                            sx={{
                              fontWeight: 400,
                              fontSize: (theme: any) => theme.typography.table.body,
                              height: 22,
                              borderRadius: '12px',
                              backgroundColor: () => {
                                const statusColor = getStatusInfo(booking.status).color;
                                if (statusColor === 'success') return 'rgba(22, 163, 74, 0.12)';
                                if (statusColor === 'warning') return 'rgba(245, 158, 11, 0.12)';
                                if (statusColor === 'error') return 'rgba(220, 38, 38, 0.12)';
                                return 'rgba(158, 158, 158, 0.12)';
                              },
                              color: () => {
                                const statusColor = getStatusInfo(booking.status).color;
                                if (statusColor === 'success') return 'rgba(22, 163, 74, 0.9)';
                                if (statusColor === 'warning') return 'rgba(245, 158, 11, 0.9)';
                                if (statusColor === 'error') return 'rgba(220, 38, 38, 0.9)';
                                return 'rgba(158, 158, 158, 0.9)';
                              },
                              border: 'none',
                              '& .MuiChip-label': {
                                px: 1.25,
                                py: 0,
                              }
                            }}
                          />
                          {hasNonGeocodedLocations(booking) && (
                            <Tooltip
                              title={t('One or more loading locations are not geocoded (missing coordinates). We won\'t be able to calculate routes, scores and destination matching accurately.')}
                              arrow
                              placement="top"
                            >
                              <Chip
                                icon={<WarningIcon sx={{ fontSize: (theme: any) => theme.typography.table.body + ' !important' }} />}
                                label={t('Not Geocoded')}
                                size="small"
                                sx={{
                                  height: 18,
                                  fontSize: (theme: any) => theme.typography.table.small,
                                  bgcolor: 'rgba(220, 38, 38, 0.15)',
                                  color: 'rgba(220, 38, 38, 0.9)',
                                  border: '1px solid rgba(220, 38, 38, 0.3)',
                                  borderRadius: '12px',
                                  '& .MuiChip-icon': {
                                    color: 'rgba(220, 38, 38, 0.9)'
                                  },
                                  '& .MuiChip-label': {
                                    px: 0.5,
                                    py: 0,
                                  }
                                }}
                              />
                            </Tooltip>
                          )}
                          {isCurrentBooking && (
                            <Tooltip
                              title={t('This booking is the starting point for auto search. Auto search will find new offers that start after this booking ends.')}
                              arrow
                              placement="top"
                            >
                              <Chip
                                label={t('Auto Search Reference')}
                                size="small"
                                sx={{
                                  height: 18,
                                  fontSize: (theme: any) => theme.typography.table.small,
                                  bgcolor: 'rgba(22, 163, 74, 0.15)',
                                  color: 'rgba(22, 163, 74, 0.9)',
                                  border: '1px solid rgba(22, 163, 74, 0.3)',
                                  fontWeight: 400,
                                  borderRadius: '9px',
                                  '& .MuiChip-label': { px: 0.75, py: 0 }
                                }}
                              />
                            </Tooltip>
                          )}
                          {isDelayed && (
                            <Tooltip
                              title={t('This booking is delayed.')}
                              arrow
                              placement="top"
                            >
                              <Chip
                                label={t('Delayed')}
                                size="small"
                                sx={{
                                  height: 18,
                                  fontSize: (theme: any) => theme.typography.table.small,
                                  bgcolor: 'rgba(220, 38, 38, 0.15)',
                                  color: 'rgba(220, 38, 38, 0.9)',
                                  border: '1px solid rgba(220, 38, 38, 0.3)',
                                  fontWeight: 400,
                                  borderRadius: '9px',
                                  '& .MuiChip-label': { px: 0.75, py: 0 }
                                }}
                              />
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ py: 1 }}>{booking.createdAt ? formatDateTime(booking.createdAt) : '-'}</TableCell>
                      <TableCell sx={{ py: 1 }} align="right">
                        <Tooltip title={t('Download loading sheet')}>
                          <IconButton
                            onClick={(e) => handleDownloadLoadingSheet(e, booking)}
                            size="small"
                            sx={{
                              color: 'text.grey',
                              '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                              }
                            }}
                          >
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t('Print loading sheet')}>
                          <IconButton
                            onClick={(e) => handlePrintLoadingSheet(e, booking)}
                            size="small"
                            sx={{
                              color: 'text.grey',
                              '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                              }
                            }}
                          >
                            <PrintIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {!isDriver && (
                          <Tooltip title={t('Delete booking')}>
                            <IconButton
                              onClick={async (e) => {
                                e.stopPropagation();
                                await handleDelete(Number(booking.id));
                                fetchBookings(vehicle?.id);
                              }}
                              size="small"
                              sx={{
                                color: 'text.red',
                                '&:hover': {
                                  backgroundColor: 'rgba(220, 38, 38, 0.08)',
                                }
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableSkeletonLoader>
          </TableBody>
        </Table>
      </TableContainer>

      {standalone && (
        <TablePagination
          component="div"
          count={rowCount}
          page={paginationModel.page}
          onPageChange={(_, page) => setPaginationModel((prev: any) => ({ ...prev, page }))}
          rowsPerPage={paginationModel.pageSize}
          onRowsPerPageChange={(e) => setPaginationModel({ page: 0, pageSize: parseInt(e.target.value, 10) })}
          rowsPerPageOptions={[5, 10, 25, 50]}
          labelRowsPerPage={t('Rows per page')}
        />
      )}

      {Dialog}
      {DeleteModal}
    </Box>
  );
}
